import { useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Brain, Browser, CalendarDots, CaretRight, ChartBar, ChartLineUp, ChatCircleDots,
  CheckSquareOffset, DotsSixVertical, Eye, EyeSlash, FileText, GitBranch,
  Moon, MagnifyingGlass, PaperPlaneTilt, PencilSimple, SignOut, Sparkle, SquaresFour, Sun,
  TrendUp, UserPlus, UsersThree, Wallet, GearSix } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { normalizeCustomPages } from '../lib/pageAccess';

const NAV_ICONS = {
  'creator-operations': UsersThree,
  'command-center': SquaresFour, scheduler: CalendarDots, chatters: ChatCircleDots,
  review: ChartBar, pipeline: GitBranch, 'ai-content': Sparkle,
  'reddit-dashboard': TrendUp, telegram: PaperPlaneTilt, documents: FileText,
  onboarding: UserPlus, finance: Wallet,
  'models-hub': UsersThree, tasks: CheckSquareOffset, 'content-os': Browser, brain: Brain,
  'sales-overview': ChartLineUp, settings: GearSix,
};

const ownerNav = [
  { path: '/creator-operations', label: 'Creator Operations', key: 'creator-operations' },
  { path: '/command-center', label: 'Command Center', key: 'command-center' },
  { path: '/reels-operations', label: 'Reels Operations', key: 'scheduler' },
  { path: '/tasks', label: 'Tasks', key: 'tasks' },
  { path: '/sales-overview', label: 'Sales Overview', key: 'sales-overview' },
  { path: '/review', label: 'Sunday Review', key: 'review' },
  { path: '/pipeline', label: 'Pipeline', key: 'pipeline' },
  { divider: true },
  { path: '/content-os', label: 'Content OS', key: 'content-os' },
  { path: '/knowledge', label: 'Knowledge', key: 'brain' },
  { path: '/ai-content', label: 'AI Generator', key: 'ai-content' },
  { path: '/reddit-operations', label: 'Reddit Operations', key: 'reddit-dashboard' },
  { divider: true },
  { path: '/telegram', label: 'Send Alert', key: 'telegram' },
  { path: '/documents', label: 'Documents', key: 'documents' },
  { path: '/onboarding', label: 'Model Onboarding', key: 'onboarding' },
  { path: '/models-hub', label: 'Models Hub', key: 'models-hub' },
  { path: '/settings', label: 'Settings', key: 'settings' },
  { divider: true },
  { path: '/finance', label: 'Finance', key: 'finance' },
];

const vaNav = [
  { path: '/creator-operations', label: 'Creator Operations', key: 'creator-operations' },
  { path: '/reels-operations', label: 'Reels Operations', key: 'scheduler' },
  { path: '/tasks', label: 'Tasks', key: 'tasks' },
  { divider: true },
  { path: '/content-os', label: 'Content OS', key: 'content-os' },
  { path: '/knowledge', label: 'Knowledge', key: 'brain' },
  { path: '/ai-content', label: 'AI Generator', key: 'ai-content' },
  { path: '/reddit-operations', label: 'Reddit Operations', key: 'reddit-dashboard' },
  { divider: true },
  { path: '/onboarding', label: 'Model Onboarding', key: 'onboarding' },
  { path: '/models-hub', label: 'Models Hub', key: 'models-hub' },
  { path: '/telegram', label: 'Send Alert', key: 'telegram' },
  { path: '/documents', label: 'Documents', key: 'documents' },
];

const chatterNav = [
  { path: '/chatters', label: 'Chatters', key: 'chatters' },
  { path: '/tasks', label: 'Tasks', key: 'tasks' },
  { divider: true },
  { path: '/knowledge', label: 'Knowledge', key: 'brain' },
  { path: '/documents', label: 'Documents', key: 'documents' },
];

const navByRole = { owner: ownerNav, va: vaNav, chatter: chatterNav };
const PAGE_LABEL_TO_KEY = {
  'Command Center': 'command-center', Scheduler: 'scheduler', 'Reels Operations': 'scheduler',
  Tasks: 'tasks', 'Chatters CRM': 'chatters', 'Sunday Review': 'review', Pipeline: 'pipeline',
  'AI Generator': 'ai-content', 'Reddit Dashboard': 'reddit-dashboard',
  'Reddit Operations': 'reddit-dashboard', 'Send Alert': 'telegram', Documents: 'documents',
  'Model Onboarding': 'onboarding', Settings: 'settings', Finance: 'finance',
  'Models Hub': 'models-hub', 'Content OS': 'content-os', 'CUZO Brain': 'brain', Knowledge: 'brain',
  'Sales Overview': 'sales-overview',
};
const OWNER_GROUPS = [
  { id: 'operations', label: 'Operations', keys: ['command-center', 'tasks', 'review', 'documents', 'telegram', 'finance'] },
  { id: 'traffic', label: 'Traffic', keys: ['scheduler', 'reddit-dashboard'] },
  { id: 'sales', label: 'Sales', keys: ['sales-overview', 'pipeline'] },
  { id: 'models', label: 'Models', keys: ['onboarding', 'models-hub', 'creator-operations'] },
  { id: 'tools', label: 'Tools', keys: ['content-os', 'brain', 'ai-content', 'settings'] },
];

function NavIcon({ navKey, size = 17 }) {
  const Component = NAV_ICONS[navKey] || SquaresFour;
  return <Component aria-hidden="true" size={size} weight="regular" />;
}

function SidebarLinks({ items }) {
  return items.map(item => (
    <NavLink key={item.path} to={item.path} title={item.label} aria-label={item.label} className={({ isActive }) => `cuzo-nav-link${isActive ? ' is-active' : ''}`}>
      <NavIcon navKey={item.key} />
      <span>{item.label}</span>
    </NavLink>
  ));
}

function SidebarGroups({ groups, location, collapsed, onToggle }) {
  return groups.map(group => {
    const hasActive = group.items.some(item => location.pathname === item.path);
    const isOpen = hasActive || !collapsed[group.id];
    return (
      <section className="cuzo-nav-group" key={group.id}>
        <button className={`cuzo-nav-group__toggle${hasActive ? ' has-active' : ''}`} onClick={() => onToggle(group.id)} aria-expanded={isOpen}>
          <span>{group.label}</span>
          <CaretRight className={`cuzo-nav-group__chevron${isOpen ? ' is-open' : ''}`} size={13} weight="bold" />
        </button>
        {isOpen && <div className="cuzo-nav-group__items"><SidebarLinks items={group.items} /></div>}
      </section>
    );
  });
}

export default function Sidebar({ width = 224, onResize }) {
  const compact = width === 64;
  const expandedWidth = useRef(compact ? 224 : width);
  const resize = next => {
    if (next > 64) expandedWidth.current = next;
    onResize(next);
  };
  const toggleCompact = () => { setEditMode(false); resize(compact ? expandedWidth.current : 64); };
  const dragResize = event => {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const normalizedPages = normalizeCustomPages(user?.custom_pages);
  const allowedKeys = normalizedPages ? normalizedPages.map(label => PAGE_LABEL_TO_KEY[label]).filter(Boolean) : null;
  const roleNav = navByRole[user?.role] || [];
  const rawNav = user?.role === 'chatter' && allowedKeys?.includes('reddit-dashboard')
    ? [...roleNav, { divider: true }, { path: '/reddit-operations', label: 'Reddit Operations', key: 'reddit-dashboard' }]
    : roleNav;
  const defaultNav = allowedKeys ? rawNav.filter(item => item.divider || allowedKeys.includes(item.key)) : rawNav;
  const defaultItems = defaultNav.filter(item => !item.divider);
  const storageKey = `cuzo_sidebar_${user?.role}`;
  const groupStorageKey = `cuzo_sidebar_groups_${user?.role}`;

  const [customization, setCustomization] = useState(() => {
    try { const saved = localStorage.getItem(storageKey); return saved ? JSON.parse(saved) : null; }
    catch (_) { return null; }
  });
  const [editMode, setEditMode] = useState(false);
  const [dragOverTarget, setDragOverTarget] = useState(null);
  const dragItem = useRef(null);
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem(groupStorageKey)) || { traffic: true, sales: true, models: true, tools: true }; }
    catch (_) { return { traffic: true, sales: true, models: true, tools: true }; }
  });

  const saveCustomization = next => { setCustomization(next); localStorage.setItem(storageKey, JSON.stringify(next)); };
  const defaultOwnerGroup = key => OWNER_GROUPS.find(group => group.keys.includes(key))?.id || 'tools';
  const defaultGroupOrders = () => Object.fromEntries(OWNER_GROUPS.map(group => [
    group.id,
    defaultItems.filter(item => defaultOwnerGroup(item.key) === group.id).map(item => item.key),
  ]));
  const enterEditMode = () => {
    if (compact) resize(expandedWidth.current);
    if (!customization) saveCustomization({ hidden: [], labels: {}, groups: {}, groupOrders: defaultGroupOrders() });
    setEditMode(true);
  };
  const resetToDefault = () => { setCustomization(null); localStorage.removeItem(storageKey); setEditMode(false); };
  const orderedKeys = customization
    ? [...(customization.order || []), ...defaultItems.map(item => item.key).filter(key => !(customization.order || []).includes(key))]
    : defaultItems.map(item => item.key);
  const withCustomLabel = item => item ? { ...item, label: customization?.labels?.[item.key] || item.label } : item;
  const displayItems = customization
    ? orderedKeys.map(key => withCustomLabel(defaultItems.find(item => item.key === key))).filter(Boolean).filter(item => !(customization.hidden || []).includes(item.key))
    : defaultNav;
  const groupForKey = key => {
    const savedGroup = customization?.groups?.[key];
    return OWNER_GROUPS.some(group => group.id === savedGroup) ? savedGroup : defaultOwnerGroup(key);
  };
  const sortGroupItems = (groupId, items) => {
    const savedOrder = customization?.groupOrders?.[groupId] || [];
    const order = [...savedOrder, ...items.map(item => item.key).filter(key => !savedOrder.includes(key))];
    return [...items].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  };
  const ownerGroups = OWNER_GROUPS.map(group => ({
    ...group,
    items: sortGroupItems(group.id, displayItems.filter(item => !item.divider && groupForKey(item.key) === group.id)),
  })).filter(group => group.items.length);
  const editGroups = OWNER_GROUPS.map(group => ({
    ...group,
    items: sortGroupItems(group.id, defaultItems.filter(item => groupForKey(item.key) === group.id).map(withCustomLabel)),
  })).filter(group => group.items.length);

  const handleDrop = (event, groupId, index) => {
    event.preventDefault();
    const dragged = dragItem.current;
    if (!dragged || dragged.groupId !== groupId || dragged.index === index) { setDragOverTarget(null); return; }
    const currentOrder = sortGroupItems(groupId, defaultItems.filter(item => groupForKey(item.key) === groupId)).map(item => item.key);
    const [moved] = currentOrder.splice(dragged.index, 1);
    currentOrder.splice(index, 0, moved);
    saveCustomization({ ...customization, groupOrders: { ...(customization?.groupOrders || defaultGroupOrders()), [groupId]: currentOrder } });
    dragItem.current = null;
    setDragOverTarget(null);
  };
  const toggleHide = key => {
    const hidden = customization?.hidden || [];
    saveCustomization({ ...customization, hidden: hidden.includes(key) ? hidden.filter(item => item !== key) : [...hidden, key] });
  };
  const renameItem = (key, value) => {
    const defaultLabel = defaultItems.find(item => item.key === key)?.label || '';
    const clean = value.replace(/\s+/g, ' ').slice(0, 32);
    const labels = { ...(customization?.labels || {}) };
    if (!clean.trim() || clean.trim() === defaultLabel) delete labels[key]; else labels[key] = clean;
    saveCustomization({ hidden: customization?.hidden || [], ...customization, labels });
  };
  const moveToGroup = (key, groupId) => {
    const priorGroup = groupForKey(key);
    if (groupId === priorGroup) return;
    const nextGroups = { ...(customization?.groups || {}), [key]: groupId };
    const nextOrders = { ...(customization?.groupOrders || defaultGroupOrders()) };
    nextOrders[priorGroup] = (nextOrders[priorGroup] || []).filter(entry => entry !== key);
    nextOrders[groupId] = [...(nextOrders[groupId] || []), key];
    saveCustomization({ ...customization, groups: nextGroups, groupOrders: nextOrders });
  };
  const toggleGroup = group => {
    setCollapsedGroups(current => {
      const next = { ...current, [group]: !current[group] };
      localStorage.setItem(groupStorageKey, JSON.stringify(next));
      return next;
    });
  };
  const handleLogout = () => { logout(); navigate('/login'); };
  const initials = (user?.name || user?.display_name || 'CU').split(/\s+/).map(part => part[0]).join('').slice(0, 2).toUpperCase();

  return (
    <aside className={`cuzo-sidebar${compact ? ' is-compact' : ''}`}>
      <button className="cuzo-sidebar__size-toggle" onClick={toggleCompact} aria-label={compact ? 'Expand sidebar' : 'Collapse sidebar'} title={compact ? 'Expand sidebar' : 'Collapse sidebar'} aria-expanded={!compact}>
        <CaretRight size={16} style={{ transform: compact ? undefined : 'rotate(180deg)' }} />
      </button>
      <div className="cuzo-sidebar__resize" role="separator" aria-label="Resize sidebar" aria-orientation="vertical" aria-valuemin={64} aria-valuemax={340} aria-valuenow={width} tabIndex={0}
        onPointerDown={dragResize}
        onPointerMove={event => {
          if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
          setEditMode(false);
          resize(event.clientX < 150 ? 64 : Math.max(200, Math.min(340, event.clientX)));
        }}
        onPointerUp={event => { if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId); }}
        onDoubleClick={toggleCompact}
        onKeyDown={event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault(); setEditMode(false);
          resize(event.key === 'Home' ? 64 : event.key === 'End' ? 340 : event.key === 'ArrowLeft' ? (width <= 200 ? 64 : width - 20) : compact ? 200 : Math.min(340, width + 20));
        }} />
      <header className="cuzo-sidebar__brand">
        <div><div className="cuzo-wordmark">CUZO</div><div className="cuzo-wordmark-sub">In-house</div></div>
        <button className="cuzo-theme-switch" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Use light theme' : 'Use dark theme'} title={theme === 'dark' ? 'Use light theme' : 'Use dark theme'}>
          {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
        </button>
      </header>

      {editMode && (
        <section className="cuzo-sidebar__edit">
          <div className="cuzo-sidebar__edit-title">Customize navigation</div>
          <p className="cuzo-sidebar__edit-copy">Move a page to any section, then drag it up or down. Saved for this owner on this device.</p>
          <div className="cuzo-sidebar__edit-actions"><button onClick={() => { setEditMode(false); setDragOverTarget(null); }}>Done</button><button onClick={resetToDefault}>Reset</button></div>
        </section>
      )}

      <button className="cuzo-quick-open" onClick={() => window.dispatchEvent(new Event('cuzo:open-navigation'))}>
        <MagnifyingGlass size={16} /><span>Go to workspace</span><kbd>⌘K</kbd>
      </button>
      <nav className="cuzo-sidebar__nav" aria-label="Primary navigation">
        {compact ? <div className="cuzo-nav-group__items"><SidebarLinks items={displayItems.filter(item => !item.divider)} /></div> : editMode ? editGroups.map(group => (
          <section className="cuzo-sidebar__edit-group" key={group.id}>
            <div className="cuzo-sidebar__edit-group-title">{group.label}</div>
            {group.items.map((item, index) => {
              const hidden = customization?.hidden?.includes(item.key);
              const target = `${group.id}:${index}`;
              return (
                <div key={item.key} className={`cuzo-sidebar__edit-row${dragOverTarget === target ? ' is-over' : ''}${hidden ? ' is-hidden' : ''}`} draggable
                  onDragStart={event => { dragItem.current = { groupId: group.id, index }; event.dataTransfer.effectAllowed = 'move'; }}
                  onDragOver={event => { event.preventDefault(); setDragOverTarget(target); }} onDrop={event => handleDrop(event, group.id, index)}
                  onDragEnd={() => { dragItem.current = null; setDragOverTarget(null); }}>
                  <DotsSixVertical aria-hidden="true" size={15} /><NavIcon navKey={item.key} />
                  <div className="cuzo-sidebar__edit-fields">
                    <input value={item.label} aria-label={`Rename ${defaultItems.find(entry => entry.key === item.key)?.label || item.label}`} maxLength={32} disabled={hidden}
                      onChange={event => renameItem(item.key, event.target.value)} onBlur={event => renameItem(item.key, event.target.value.trim())} />
                    <select aria-label={`Move ${item.label} to section`} value={group.id} onChange={event => moveToGroup(item.key, event.target.value)} disabled={hidden}>
                      {OWNER_GROUPS.map(destination => <option key={destination.id} value={destination.id}>{destination.label}</option>)}
                    </select>
                  </div>
                  <button className={`cuzo-sidebar__visibility${hidden ? '' : ' is-danger'}`} onClick={() => toggleHide(item.key)} aria-label={hidden ? `Show ${item.label}` : `Hide ${item.label}`}>
                    {hidden ? <Eye size={14} /> : <EyeSlash size={14} />}
                  </button>
                </div>
              );
            })}
          </section>
        )) : user?.role === 'owner' ? (
          <SidebarGroups groups={ownerGroups} location={location} collapsed={collapsedGroups} onToggle={toggleGroup} />
        ) : <div className="cuzo-nav-group__items"><SidebarLinks items={displayItems.filter(item => !item.divider)} /></div>}
      </nav>

      <footer className="cuzo-sidebar__footer">
        {!editMode && user?.role === 'owner' && <button className="cuzo-sidebar__customize" onClick={enterEditMode}><PencilSimple size={15} /><span>Customize navigation</span></button>}
        <div className="cuzo-sidebar__identity"><div className="cuzo-sidebar__avatar">{initials}</div><div><b>{user?.name || user?.display_name || 'CUZO team'}</b><small>{user?.role}</small></div></div>
        <button className="cuzo-sidebar__signout" onClick={handleLogout}><SignOut size={14} style={{ marginRight: 7, verticalAlign: '-2px' }} />Sign out</button>
      </footer>
    </aside>
  );
}
